import {
  Bar,
  BarChart,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TickProcessed } from './types';
import { abbreviateBalance } from '@/presentation/utils/abbreviateBalance';
import { useTranslations } from 'use-intl';
import { TICK_PRICE_BASE, Token } from 'nftx.js';
import { useNetwork } from '@nftx/react';
import { useState } from 'react';
import { isAddressWeth } from '@/domain/ticks/helpers';

const LiquidityChart = ({
  data,
  activeTick,
  tickLower,
  tickUpper,
  inputTokens,
  liquidityBase,
}: {
  data: TickProcessed[];
  activeTick: number;
  tickLower?: bigint;
  tickUpper?: bigint;
  liquidityBase: number;
  inputTokens: Token[];
}) => {
  const network = useNetwork();
  const t = useTranslations('Pool.Charts.Liquidity');

  const hasUserLiq = tickLower != null && tickUpper != null;

  const token0 = inputTokens[0];
  const token1 = inputTokens[1];
  const isWeth0 = isAddressWeth(token0.id, network);
  const [flipAxis, setFlipAxis] = useState(isWeth0);
  const toggleAxis = () => {
    setFlipAxis((prev) => !prev);
  };
  const tokenA = flipAxis ? token1 : token0;
  const tokenB = flipAxis ? token0 : token1;

  return (
    <ResponsiveContainer width={'100%'} height={300}>
      <BarChart margin={{ bottom: 0 }} data={data}>
        <Tooltip
          content={({ payload }) => (
            <div className="rounded-md border border-gray-300 p-3 text-sm bg-gray-50">
              <p className="font-medium mb-1">{t('tooltip.title')}</p>
              <dl className="text-xs space-y-1">
                <div>
                  <dt>
                    {t('tooltip.oneToken', {
                      symbol: token0.symbol,
                    })}
                  </dt>
                  <dd>
                    {t('tooltip.equals', {
                      amount: abbreviateBalance(
                        TICK_PRICE_BASE ** payload?.[0]?.payload?.tickIdx
                      ),
                      symbol: token1.symbol,
                    })}
                  </dd>
                </div>
                <div>
                  <dt>{t('tooltip.oneToken', { symbol: token1.symbol })}</dt>
                  <dd>
                    {t('tooltip.equals', {
                      amount: abbreviateBalance(
                        1 / TICK_PRICE_BASE ** payload?.[0]?.payload?.tickIdx
                      ),
                      symbol: token0.symbol,
                    })}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        />
        <Bar dataKey="activeLiquidity" radius={1} stackId={'pool'}>
          {data?.map((x) => (
            <Cell
              key={x.tickIdx}
              fill={x.tickIdx === activeTick ? '#EB4C7A' : '#EB4C7A50'}
              width={x.tickIdx === activeTick ? 4 : 3}
            />
          ))}
        </Bar>
        {hasUserLiq && (
          <Bar dataKey="userLiquidity" radius={1} stackId={'pool'}>
            {data?.map((x) => {
              return (
                <Cell
                  key={x.tickIdx}
                  fill={x.tickIdx === activeTick ? '#EB4C7A' : '#14B8A6'}
                  width={x.tickIdx === activeTick ? 4 : 3}
                />
              );
            })}
          </Bar>
        )}

        <XAxis
          dataKey={'tickIdx'}
          axisLine={false}
          tickMargin={16}
          minTickGap={24}
          interval={'preserveStartEnd'}
          tickSize={0}
          fontSize={10}
          tickFormatter={(val) => {
            if (flipAxis) {
              return abbreviateBalance(1 / TICK_PRICE_BASE ** val);
            }
            return abbreviateBalance(TICK_PRICE_BASE ** val);
          }}
        />
        <YAxis
          domain={[liquidityBase, 'auto']}
          tick={false}
          axisLine={false}
          width={0}
        />
        <Legend
          content={() => (
            <p
              role="button"
              className="text-xs text-center text-gray-500"
              onClick={toggleAxis}
            >
              {t('legend', {
                tokenB: tokenB.symbol,
                tokenA: tokenA.symbol,
              })}
            </p>
          )}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default LiquidityChart;
